import { useNavigate, useLocation } from "react-router-dom";
import { LayoutGrid, PlusCircle, Settings, Sparkles, Camera, FileText, Scale, MoreHorizontal, LifeBuoy } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const portfolio = [
  { icon: LayoutGrid, label: "Properties", path: "/dashboard", testId: "nav-dashboard" },
  { icon: PlusCircle, label: "New", path: "/start", testId: "nav-start", primary: true },
  { icon: Settings, label: "Settings", path: "/settings", testId: "nav-settings" },
];

const buckets = [
  { icon: Sparkles, label: "Right now", tab: "right-now", testId: "nav-right-now" },
  { icon: Camera, label: "Document", tab: "document", testId: "nav-document" },
  { icon: FileText, label: "Records", tab: "records", testId: "nav-records" },
  { icon: Scale, label: "Respond", tab: "respond", testId: "nav-respond" },
];

// Detect the active property id from any property-scoped route so we can show ONE in-property bar.
function usePropertyId(pathname) {
  const m =
    pathname.match(/\/property\/([^/]+)/) ||
    pathname.match(/\/(?:capture|compare|coverage|self-audit|move-out-day|vault|key-return-capsule|condition-history|tenancy-timeline)\/([^/]+)/) ||
    pathname.match(/\/properties\/([^/]+)\//);
  return m ? m[1] : null;
}

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const pid = usePropertyId(location.pathname);

  if (pid) {
    const onProperty = location.pathname === `/property/${pid}`;
    const activeTab = new URLSearchParams(location.search).get("tab") || "right-now";
    return (
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-[#1E293B] bg-[#0B1220]/95 backdrop-blur-xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="bottom-nav-property">
        <div className="grid grid-cols-5 max-w-md mx-auto">
          {buckets.map((it) => {
            const active = onProperty && activeTab === it.tab;
            const Icon = it.icon;
            return (
              <button key={it.tab} data-testid={it.testId} onClick={() => navigate(`/property/${pid}?tab=${it.tab}`)}
                className="flex flex-col items-center justify-center gap-1 py-2.5">
                <Icon size={20} strokeWidth={2} className={active ? "text-brand" : "text-[#64748B]"} />
                <span className={`text-[10px] font-medium ${active ? "text-brand" : "text-[#94A3B8]"}`}>{it.label}</span>
              </button>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-more" className="flex flex-col items-center justify-center gap-1 py-2.5">
                <MoreHorizontal size={20} strokeWidth={2} className="text-[#64748B]" />
                <span className="text-[10px] font-medium text-[#94A3B8]">More</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="mb-2">
              <DropdownMenuItem data-testid="more-properties" onClick={() => navigate("/dashboard")}><LayoutGrid size={16} className="mr-2" /> Properties</DropdownMenuItem>
              <DropdownMenuItem data-testid="more-new" onClick={() => navigate("/start")}><PlusCircle size={16} className="mr-2" /> New property</DropdownMenuItem>
              <DropdownMenuItem data-testid="more-settings" onClick={() => navigate("/settings")}><Settings size={16} className="mr-2" /> Settings</DropdownMenuItem>
              <DropdownMenuItem data-testid="more-help" onClick={() => navigate("/help")}><LifeBuoy size={16} className="mr-2" /> Help & sample</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    );
  }

  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-[#E2E8F0] bg-white/90 backdrop-blur-xl"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="bottom-nav">
      <div className="grid grid-cols-3 max-w-md mx-auto">
        {portfolio.map((it) => {
          const active = location.pathname === it.path;
          const Icon = it.icon;
          return (
            <button key={it.path} data-testid={it.testId} onClick={() => navigate(it.path)}
              className="flex flex-col items-center justify-center gap-1 py-2.5 transition-colors">
              {it.primary ? (
                <span className="grid place-items-center h-11 w-11 -mt-6 rounded-full bg-brand text-white shadow-[0_6px_20px_rgba(15,27,45,0.32)]">
                  <Icon size={24} strokeWidth={2} />
                </span>
              ) : (
                <Icon size={22} strokeWidth={2} className={active ? "text-brand" : "text-[#94A3B8]"} />
              )}
              <span className={`text-[11px] font-medium ${active ? "text-brand" : "text-[#94A3B8]"}`}>{it.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
